/**
 * Personnel Detail Page - Sprint 6
 * Sprint 11A: Updated with Layout component and Avida branding
 * Tabs: Profile, Training, Compliance
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { personnelAPI, trainingAPI } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Clock,
  Shield,
  FileText,
  Archive,
  ExternalLink,
  RefreshCw,
} from "lucide-react";

const STATUS_COLORS = {
  Active: "bg-green-100 text-green-800 border-green-200",
  OnLeave: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Terminated: "bg-gray-100 text-gray-600 border-gray-200",
  Archived: "bg-red-100 text-red-700 border-red-200",
};

const TRAINING_STATUS_COLORS = {
  Valid: "bg-green-100 text-green-800 border-green-200",
  Expired: "bg-red-100 text-red-800 border-red-200",
  ExpiringSoon: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Missing: "bg-gray-100 text-gray-600 border-gray-200",
  Waived: "bg-purple-100 text-purple-800 border-purple-200",
};

const TYPE_LABELS = {
  Caregiver: "Caregiver",
  RN: "RN",
  OfficeStaff: "Office Staff",
  Contractor: "Contractor",
};

const PersonnelDetailPage = () => {
  const { personnelId } = useParams();
  const navigate = useNavigate();
  const { user, logout, canManagePersonnel, canManageTrainings } = useAuth();
  const [personnel, setPersonnel] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Modals
  const [showAddTrainingModal, setShowAddTrainingModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Forms
  const [trainingForm, setTrainingForm] = useState({
    requirement_code: "",
    completed_date: "",
    notes: "",
  });
  const [archiveReason, setArchiveReason] = useState("");
  const [newStatus, setNewStatus] = useState("");
  
  const canManage = canManagePersonnel;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [personnelRes, trainingsRes, complianceRes, requirementsRes] = await Promise.all([
        personnelAPI.get(personnelId),
        personnelAPI.getTrainings(personnelId),
        personnelAPI.getCompliance(personnelId, asOfDate),
        trainingAPI.getRequirements(),
      ]);
      
      setPersonnel(personnelRes.data);
      setTrainings(trainingsRes.data.trainings || []);
      setCompliance(complianceRes.data);
      setRequirements(requirementsRes.data.requirements || []);
    } catch (error) {
      toast.error("Failed to load personnel");
      navigate("/personnel");
    } finally {
      setLoading(false);
    }
  }, [personnelId, asOfDate, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddTraining = async (e) => {
    e.preventDefault();
    try {
      await personnelAPI.addTraining(personnelId, trainingForm);
      toast.success("Training added");
      setShowAddTrainingModal(false);
      setTrainingForm({ requirement_code: "", completed_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add training");
    }
  };

  const handleVerifyTraining = async (trainingId) => {
    try {
      await personnelAPI.verifyTraining(personnelId, trainingId);
      toast.success("Training verified");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify training");
    }
  };

  const handleStatusChange = async () => {
    try {
      await personnelAPI.update(personnelId, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      setShowStatusModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change status");
    }
  };

  const handleArchive = async () => {
    try {
      await personnelAPI.archive(personnelId, archiveReason);
      toast.success("Personnel archived");
      navigate("/personnel");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  // Get expiration preview for selected requirement
  const getExpirationPreview = () => {
    if (!trainingForm.requirement_code || !trainingForm.completed_date) return null;
    const req = requirements.find(r => r.code === trainingForm.requirement_code);
    if (!req || !req.expires_months) return "No expiration";
    
    const completedDate = new Date(trainingForm.completed_date);
    completedDate.setMonth(completedDate.getMonth() + req.expires_months);
    if (req.grace_days) completedDate.setDate(completedDate.getDate() + req.grace_days);
    return `Expires: ${completedDate.toLocaleDateString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!personnel) return null;

  const isArchived = !!personnel.archived_at;

  return (
    <Layout title={personnel.full_name || "Personnel Details"}>
      <div data-testid="personnel-detail-page">
        {/* Back button and status badges */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/personnel")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Personnel
            </Button>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_COLORS[personnel.status]} variant="outline">{personnel.status}</Badge>
              {personnel.is_compliant ? (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" /> Compliant
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Non-Compliant
                </Badge>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-avida-blue-50 data-[state=active]:text-avida-blue-700">
              Profile
            </TabsTrigger>
            <TabsTrigger value="training" data-testid="tab-training" className="data-[state=active]:bg-avida-blue-50 data-[state=active]:text-avida-blue-700">
              Training
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance" className="data-[state=active]:bg-avida-blue-50 data-[state=active]:text-avida-blue-700">
              Compliance
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-gray-900">
                      Personnel Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">Type</Label>
                        <p className="font-medium text-gray-900">{TYPE_LABELS[personnel.personnel_type] || personnel.personnel_type}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Status</Label>
                        <div>
                          <Badge className={STATUS_COLORS[personnel.status]} variant="outline">{personnel.status}</Badge>
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Hire Date</Label>
                        <p className="font-medium text-gray-900">{personnel.hire_date || "-"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Termination Date</Label>
                        <p className="font-medium text-gray-900">{personnel.termination_date || "-"}</p>
                      </div>
                    </div>

                    {personnel.user_email && (
                      <div>
                        <Label className="text-gray-500">Linked User Account</Label>
                        <p className="font-medium text-gray-900">{personnel.user_email}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                {/* Actions Card */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {isArchived ? (
                      <div className="text-center text-gray-500 py-4">
                        <Archive className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="font-medium">This record is archived</p>
                        <p className="text-sm">{personnel.archived_reason}</p>
                      </div>
                    ) : canManage && (
                      <>
                        {personnel.status !== "Terminated" && (
                          <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="w-full" data-testid="change-status-btn">
                                Change Status
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Change Personnel Status</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <Select value={newStatus} onValueChange={setNewStatus}>
                                  <SelectTrigger data-testid="new-status-select">
                                    <SelectValue placeholder="Select new status" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Active">Active</SelectItem>
                                    <SelectItem value="OnLeave">On Leave</SelectItem>
                                    <SelectItem value="Terminated">Terminated</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  onClick={handleStatusChange}
                                  disabled={!newStatus}
                                  className="w-full bg-blue-600 hover:bg-blue-700"
                                  data-testid="confirm-status-btn"
                                >
                                  Change Status
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}

                        <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" data-testid="archive-btn">
                              <Archive className="h-4 w-4 mr-2" /> Archive
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Archive Personnel</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <p className="text-sm text-gray-600">
                                Archiving is for exceptional cases (duplicates, created in error). 
                                Use status changes for normal lifecycle.
                              </p>
                              <div>
                                <Label>Archive Reason * (min 10 chars)</Label>
                                <Textarea
                                  value={archiveReason}
                                  onChange={(e) => setArchiveReason(e.target.value)}
                                  placeholder="Reason for archiving..."
                                  data-testid="archive-reason-input"
                                />
                              </div>
                              <Button
                                onClick={handleArchive}
                                disabled={archiveReason.length < 10}
                                variant="destructive"
                                className="w-full"
                                data-testid="confirm-archive-btn"
                              >
                                Archive Personnel
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Metadata */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-sm text-gray-700">Metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Created</span>
                      <span className="text-gray-700">{formatDate(personnel.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">ID</span>
                      <span className="text-xs font-mono text-gray-600">{personnel.id.substring(0, 8)}...</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Training Tab */}
          <TabsContent value="training">
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Training Records
                </CardTitle>
                {canManageTrainings && !isArchived && (
                  <Dialog open={showAddTrainingModal} onOpenChange={setShowAddTrainingModal}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-training-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add Training
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Training Completion</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddTraining} className="space-y-4">
                        <div>
                          <Label>Training Requirement *</Label>
                          <Select
                            value={trainingForm.requirement_code}
                            onValueChange={(v) => setTrainingForm({ ...trainingForm, requirement_code: v })}
                          >
                            <SelectTrigger data-testid="training-requirement-select">
                              <SelectValue placeholder="Select requirement" />
                            </SelectTrigger>
                            <SelectContent>
                              {requirements
                                .filter(r => r.applies_to_types.includes(personnel.personnel_type))
                                .map((r) => (
                                  <SelectItem key={r.code} value={r.code}>
                                    {r.name} {r.expires_months ? `(${r.expires_months} mo)` : "(No expiration)"}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Completion Date *</Label>
                          <Input
                            type="date"
                            value={trainingForm.completed_date}
                            onChange={(e) => setTrainingForm({ ...trainingForm, completed_date: e.target.value })}
                            required
                            data-testid="training-date-input"
                          />
                          {getExpirationPreview() && (
                            <p className="text-xs text-blue-600 mt-1">{getExpirationPreview()}</p>
                          )}
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea
                            value={trainingForm.notes}
                            onChange={(e) => setTrainingForm({ ...trainingForm, notes: e.target.value })}
                            placeholder="Optional notes..."
                            data-testid="training-notes-input"
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          disabled={!trainingForm.requirement_code || !trainingForm.completed_date}
                          data-testid="submit-training-btn"
                        >
                          Add Training
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {trainings.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    No training records
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold text-gray-700">Training</TableHead>
                        <TableHead className="font-semibold text-gray-700">Completed</TableHead>
                        <TableHead className="font-semibold text-gray-700">Expires</TableHead>
                        <TableHead className="font-semibold text-gray-700">Status</TableHead>
                        <TableHead className="font-semibold text-gray-700">Verified</TableHead>
                        <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trainings.map((t) => (
                        <TableRow key={t.id} data-testid={`training-row-${t.id}`}>
                          <TableCell className="font-medium text-gray-900">{t.requirement_name || t.requirement_code}</TableCell>
                          <TableCell className="text-gray-700">{t.completed_date}</TableCell>
                          <TableCell>
                            {t.expiration_date ? (
                              <div className="text-gray-700">
                                {t.expiration_date}
                                {t.days_until_expiration !== null && (
                                  <span className={`text-xs ml-1 ${
                                    t.days_until_expiration < 0 ? "text-red-600" :
                                    t.days_until_expiration < 30 ? "text-yellow-600" : "text-gray-500"
                                  }`}>
                                    ({t.days_until_expiration < 0 ? "Expired" : `${t.days_until_expiration}d`})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">Never</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={TRAINING_STATUS_COLORS[t.status]} variant="outline">{t.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {t.verified_at ? (
                              <div className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-xs">{t.verified_by_name}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {canManageTrainings && !t.verified_at && !isArchived && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  onClick={() => handleVerifyTraining(t.id)}
                                  data-testid={`verify-training-${t.id}`}
                                >
                                  <Shield className="h-4 w-4 mr-1" /> Verify
                                </Button>
                              )}
                              {t.proof_document_id && (
                                <Button variant="ghost" size="sm" className="text-gray-500">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-900">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Compliance Status
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-500">As of date:</Label>
                    <Input
                      type="date"
                      value={asOfDate}
                      onChange={(e) => setAsOfDate(e.target.value)}
                      className="w-40"
                      data-testid="compliance-date-input"
                    />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {compliance && (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className={`p-4 rounded-lg border ${compliance.is_compliant ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-300"}`}>
                      <div className="flex items-center gap-2">
                        {compliance.is_compliant ? (
                          <>
                            <CheckCircle className="h-6 w-6 text-green-600" />
                            <span className="text-lg font-semibold text-green-700">Compliant</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-6 w-6 text-yellow-600" />
                            <span className="text-lg font-semibold text-yellow-700">Non-Compliant</span>
                          </>
                        )}
                        <span className="text-sm text-gray-500 ml-2">as of {compliance.as_of_date}</span>
                      </div>

                      {compliance.missing_requirements.length > 0 && (
                        <div className="mt-3">
                          <span className="text-sm font-medium text-red-700">Missing: </span>
                          <span className="text-sm text-red-600">{compliance.missing_requirements.join(", ")}</span>
                        </div>
                      )}

                      {compliance.expired_requirements.length > 0 && (
                        <div className="mt-2">
                          <span className="text-sm font-medium text-red-700">Expired: </span>
                          <span className="text-sm text-red-600">{compliance.expired_requirements.join(", ")}</span>
                        </div>
                      )}

                      {compliance.expiring_soon.length > 0 && (
                        <div className="mt-2">
                          <span className="text-sm font-medium text-yellow-700">Expiring Soon: </span>
                          <span className="text-sm text-yellow-600">{compliance.expiring_soon.join(", ")}</span>
                        </div>
                      )}
                    </div>

                    {/* Detail Table */}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold text-gray-700">Requirement</TableHead>
                          <TableHead className="font-semibold text-gray-700">Status</TableHead>
                          <TableHead className="font-semibold text-gray-700">Completed</TableHead>
                          <TableHead className="font-semibold text-gray-700">Expires</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {compliance.requirements_detail.map((r) => (
                          <TableRow key={r.code}>
                            <TableCell className="font-medium text-gray-900">{r.name}</TableCell>
                            <TableCell>
                              <Badge className={TRAINING_STATUS_COLORS[r.status]} variant="outline">{r.status}</Badge>
                            </TableCell>
                            <TableCell className="text-gray-700">{r.completed_date || "-"}</TableCell>
                            <TableCell>
                              {r.expiration_date ? (
                                <div className="text-gray-700">
                                  {r.expiration_date}
                                  {r.days_until_expiration !== null && (
                                    <span className={`text-xs ml-1 ${
                                      r.days_until_expiration < 0 ? "text-red-600" :
                                      r.days_until_expiration < 30 ? "text-yellow-600" : "text-gray-500"
                                    }`}>
                                      ({r.days_until_expiration < 0 ? `${Math.abs(r.days_until_expiration)}d ago` : `${r.days_until_expiration}d`})
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">Never</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default PersonnelDetailPage;
